// دسترسی مؤثر از GET /api/v1/identity/me/access (AP-006): Permissionها، Featureها، Limitها، حالت دسترسی و شعب.
import { unwrap, type AccessMode, type LimitAccess, type MeAccessResponse } from '@phoenix/api-client';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useTenantSession } from './TenantSessionProvider';

export interface AccessValue {
  isLoading: boolean;
  error: unknown;
  /** Permission مؤثر عضویت در مستأجر جاری ({ctx}.{resource}.{action}). */
  can: (permission: string) => boolean;
  /** Feature مؤثر (feature.{ctx}.{name}). */
  featureEnabled: (feature: string) => boolean;
  accessMode: AccessMode;
  activeBranchId: string | null;
  branchIds: readonly string[];
  limits: readonly LimitAccess[];
  /** مقدار مؤثر iam.session_lock_minutes (۱..۳۰) از me/access؛ پیش از پاسخ undefined (C-AGENT-185، C-AGENT-224). */
  sessionLockMinutes: number | undefined;
  /** واحد نمایش پول و منطقه زمانی مستأجر از me/access (برای همه اعضا، بدون cfg.settings.view). */
  display: MeAccessResponse['display'] | undefined;
  refetch: () => void;
}

export function accessQueryKey(tenantId: string | undefined, branchId: string | null | undefined) {
  return ['identity', 'me', 'access', tenantId ?? null, branchId ?? null] as const;
}

/** ساخت توابع دسترسی از پاسخ me/access (منطق خالص؛ تست واحد useAccess.can). */
export function createAccess(data: MeAccessResponse | undefined, lockedByApi = false) {
  const permissions = new Set(data?.permissions ?? []);
  const features = new Map((data?.features ?? []).map((f) => [f.key, f.enabled]));
  const reported = (data?.accessMode ?? 'Full') as AccessMode;
  return {
    can: (permission: string) => permissions.has(permission),
    featureEnabled: (feature: string) => features.get(feature) === true,
    // پاسخ 423 در میانه نشست (پیش از بازخوانی me/access) هم بنر را فعال می‌کند.
    accessMode: (lockedByApi && reported === 'Full' ? 'ReadOnly' : reported) as AccessMode,
  };
}

const AccessContext = createContext<AccessValue | null>(null);

export function AccessProvider({ children }: { children: ReactNode }) {
  const { tenantApi, session, status, notices } = useTenantSession();
  const enabled = status === 'active' && session !== null;

  const query = useQuery({
    queryKey: accessQueryKey(session?.tenantId, session?.activeBranchId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/identity/me/access')),
    enabled,
    staleTime: 60_000,
  });

  const value = useMemo<AccessValue>(() => {
    const access = createAccess(query.data, notices.locked !== null);
    return {
      isLoading: query.isPending,
      error: query.error,
      ...access,
      activeBranchId: query.data?.activeBranchId ?? session?.activeBranchId ?? null,
      branchIds: query.data?.branchIds ?? session?.branchIds ?? [],
      limits: query.data?.limits ?? [],
      sessionLockMinutes:
        query.data?.sessionLockMinutes === undefined ? undefined : Number(query.data.sessionLockMinutes),
      display: query.data?.display,
      refetch: () => void query.refetch(),
    };
  }, [query, session, notices.locked]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess(): AccessValue {
  const context = useContext(AccessContext);
  if (!context) {
    throw new Error('useAccess must be used inside <AccessProvider>.');
  }
  return context;
}

export interface WriteAccess {
  /** نوشتن مجاز است (Permission و حالت دسترسی Full). */
  canWrite: boolean;
  /** Permission نوشتن را دارد. */
  permitted: boolean;
  /** Permission دارد ولی حالت دسترسی Full نیست (ReadOnly/Locked — AC-PLT-004.1). */
  blockedByAccessMode: boolean;
}

/**
 * دکمه‌ها و فرم‌های نوشتن (AP-PLT-02): بدون Permission یا در حالت دسترسی غیر Full غیرفعال می‌شوند تا کاربر پیش از 423
 * ببیند چرا اقدام ممکن نیست؛ تصمیم نهایی با سرور است.
 */
export function useWriteAccess(permission: string): WriteAccess {
  const access = useAccess();
  const permitted = access.can(permission);
  const full = access.accessMode === 'Full';
  return { canWrite: permitted && full, permitted, blockedByAccessMode: permitted && !full };
}

/** نسخه آزاد برای اجزایی که ممکن است بیرون از AccessProvider باشند. */
export function useOptionalAccess(): AccessValue | null {
  return useContext(AccessContext);
}
